import * as React from "react";
import {
  Button,
  makeStyles,
  shorthands,
  Text,
} from "@fluentui/react-components";
import {
  CheckmarkCircle24Regular,
  Delete24Regular,
  Document24Regular,
  ArrowUpload24Regular,
} from "@fluentui/react-icons";

interface DocumentUploadCardProps {
  label: string;
  description: string;
  accept?: string;
  fileNames?: string[];
  isUploading?: boolean;
  error?: string | null;
  multiple?: boolean;
  onFileChange: (files: File[] | null) => void;
  onRemoveFile?: (index: number) => void;
}

const useStyles = makeStyles({
  card: {
    ...shorthands.border("1px", "dashed", "#2f3338"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("12px"),
    alignItems: "center",
    backgroundColor: "#0f1114",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    minHeight: "104px",
    textAlign: "center",
    transitionDuration: "120ms",
    transitionProperty: "background-color, border-color",
    transitionTimingFunction: "ease",
    ":hover": {
      ...shorthands.border("1px", "dashed", "#3c444c"),
      backgroundColor: "#14181d",
    },
  },
  dragging: {
    ...shorthands.border("1px", "dashed", "#38bdf8"),
    backgroundColor: "#0b2530",
  },
  selected: {
    ...shorthands.border("1px", "solid", "#29313a"),
    alignItems: "stretch",
    backgroundColor: "#11161b",
    cursor: "default",
    minHeight: "auto",
    paddingBottom: "10px",
    paddingTop: "10px",
    textAlign: "left",
  },
  selectedDropTarget: {
    ...shorthands.border("1px", "dashed", "#2f3338"),
    ":hover": {
      ...shorthands.border("1px", "dashed", "#3c444c"),
      backgroundColor: "#14181d",
    },
  },
  iconBox: {
    ...shorthands.borderRadius("7px"),
    alignItems: "center",
    backgroundColor: "#1b2026",
    color: "#9aa4af",
    display: "flex",
    height: "34px",
    justifyContent: "center",
    width: "34px",
    "& svg": {
      height: "20px",
      width: "20px",
    },
  },
  browse: {
    color: "#38bdf8",
    fontWeight: 600,
  },
  label: {
    color: "#f7f8fa",
    lineHeight: "18px",
  },
  description: {
    color: "#a9b3be",
    lineHeight: "17px",
  },
  prompt: {
    color: "#9aa4af",
    lineHeight: "17px",
  },
  selectedRow: {
    alignItems: "center",
    display: "flex",
    gap: "10px",
    justifyContent: "space-between",
  },
  selectedText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  selectedLabel: {
    color: "#f7f8fa",
  },
  fileName: {
    color: "#a9b3be",
    overflowX: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  error: {
    color: "#ff8a8a",
    lineHeight: "17px",
  },
  fileList: {
    display: "flex",
    flexDirection: "column",
    gap: "7px",
    marginTop: "8px",
  },
  selectedActions: {
    display: "flex",
    gap: "8px",
    marginTop: "10px",
  },
  addButton: {
    flexGrow: 1,
  },
  fileItem: {
    alignItems: "center",
    backgroundColor: "#0b0e11",
    borderRadius: "6px",
    display: "flex",
    gap: "8px",
    justifyContent: "space-between",
    minWidth: 0,
    paddingBottom: "6px",
    paddingLeft: "8px",
    paddingRight: "4px",
    paddingTop: "6px",
  },
  hiddenInput: {
    display: "none",
  },
});

export function DocumentUploadCard({
  label,
  description,
  accept = ".pdf,.doc,.docx,.txt",
  fileNames = [],
  isUploading = false,
  error = null,
  multiple = false,
  onFileChange,
  onRemoveFile,
}: DocumentUploadCardProps) {
  const styles = useStyles();
  const [isDragging, setIsDragging] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const inputId = React.useId();

  const selectFiles = (fileList?: FileList | null) => {
    const files = Array.from(fileList || []);
    if (files.length > 0) {
      onFileChange(multiple ? files : [files[0]]);
    }
  };

  const clearFile = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onFileChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  if (fileNames.length > 0) {
    return (
      <div
        className={`${styles.card} ${styles.selected} ${multiple ? styles.selectedDropTarget : ""} ${
          isDragging ? styles.dragging : ""
        }`}
        onDragOver={(event) => {
          if (!multiple) {
            return;
          }
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(event) => {
          if (!multiple) {
            return;
          }
          event.preventDefault();
          setIsDragging(false);
          selectFiles(event.dataTransfer.files);
        }}
      >
        <input
          ref={fileInputRef}
          id={inputId}
          className={styles.hiddenInput}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(event) => {
            selectFiles(event.target.files);
            event.currentTarget.value = "";
          }}
        />
        <div className={styles.selectedRow}>
          <div className={styles.selectedRow}>
            <div className={styles.iconBox}>
              <CheckmarkCircle24Regular />
            </div>
            <div className={styles.selectedText}>
              <Text weight="semibold" className={styles.selectedLabel}>
                {label}
              </Text>
              <Text size={200} className={styles.fileName}>
                {isUploading ? "Uploading..." : multiple ? `${fileNames.length} files selected` : fileNames[0]}
              </Text>
            </div>
          </div>
          <Button
            aria-label={`Remove ${label}`}
            appearance="subtle"
            icon={<Delete24Regular />}
            onClick={clearFile}
          />
        </div>
        {multiple ? (
          <div className={styles.fileList}>
            {fileNames.map((name, index) => (
              <div className={styles.fileItem} key={`${name}-${index}`}>
                <Text size={200} className={styles.fileName}>
                  {name}
                </Text>
                <Button
                  aria-label={`Remove ${name}`}
                  appearance="subtle"
                  size="small"
                  icon={<Delete24Regular />}
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemoveFile?.(index);
                  }}
                />
              </div>
            ))}
          </div>
        ) : null}
        {multiple ? (
          <div className={styles.selectedActions}>
            <Button
              appearance="secondary"
              className={styles.addButton}
              icon={<ArrowUpload24Regular />}
              onClick={(event) => {
                event.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              Add another reference
            </Button>
          </div>
        ) : null}
        {error ? (
          <Text size={200} className={styles.error}>
            {error}
          </Text>
        ) : null}
      </div>
    );
  }

  return (
    <label
      htmlFor={inputId}
      className={`${styles.card} ${isDragging ? styles.dragging : ""}`}
      onDragOver={(event) => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setIsDragging(false);
        selectFiles(event.dataTransfer.files);
      }}
    >
      <input
        ref={fileInputRef}
        id={inputId}
        className={styles.hiddenInput}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(event) => selectFiles(event.target.files)}
      />
      <div className={styles.iconBox}>
        {isDragging ? <ArrowUpload24Regular /> : <Document24Regular />}
      </div>
      <Text weight="semibold" className={styles.label}>
        {label}
      </Text>
      <Text size={200} className={styles.description}>
        {description}
      </Text>
      <Text size={200} className={styles.prompt}>
        {multiple ? "Drop files or " : "Drag and drop or "}
        <span className={styles.browse}>click to browse</span>
      </Text>
    </label>
  );
}
